import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { formatTime } from '../../utils/dashboardDates';
import { STATUS_GROUPS, STATUS_LABELS, getStatusGroup, getDurationMinutes } from '../../utils/daySchedule';
import styles from './DayTimeline.module.css';

const MAX_ROWS = 4;
const NO_RESOURCE = 'none';

function minutesOfDay(date) {
  const d = new Date(date);
  return d.getHours() * 60 + d.getMinutes();
}

/** Janela do gráfico: pelo menos 8h-18h, aberta até a primeira e a última consulta. */
function getRange(appointments) {
  let start = 8 * 60;
  let end = 18 * 60;
  appointments.forEach((a) => {
    const from = minutesOfDay(a.date);
    start = Math.min(start, Math.floor(from / 60) * 60);
    end = Math.max(end, Math.ceil((from + getDurationMinutes(a.duration)) / 60) * 60);
  });
  return { start, end: Math.min(end, 24 * 60) };
}

/**
 * Gráfico do dia: uma faixa por profissional com as consultas coloridas pelo
 * status, a linha de "agora" e a contagem por status.
 */
function DayTimeline({ appointments, showResource, canAttend, ownResourceId = null }) {
  const navigate = useNavigate();
  const { GetPatientById, GetResourceById } = DataProviderInstance();

  const range = useMemo(() => getRange(appointments), [appointments]);
  const span = range.end - range.start;
  const toPercent = (minutes) => ((minutes - range.start) / span) * 100;

  const rows = useMemo(() => {
    const byResource = new Map();
    appointments.forEach((a) => {
      const key = a.resource ?? NO_RESOURCE;
      if (!byResource.has(key)) byResource.set(key, []);
      byResource.get(key).push(a);
    });
    const all = [...byResource.entries()].map(([key, list]) => ({
      key,
      label: key === NO_RESOURCE ? 'Sem agenda' : (GetResourceById(key)?.Label || 'Agenda'),
      list,
    }));
    // A agenda de quem está vendo vem primeiro, para nunca ficar escondida em "+N agendas"
    if (ownResourceId == null) return all;
    const own = all.filter((row) => String(row.key) === String(ownResourceId));
    return [...own, ...all.filter((row) => String(row.key) !== String(ownResourceId))];
  }, [appointments, GetResourceById, ownResourceId]);

  const counts = STATUS_GROUPS
    .map((g) => ({ ...g, count: appointments.filter((a) => g.statuses.includes(a.status)).length }))
    .filter((g) => g.count > 0);

  const step = span > 8 * 60 ? 120 : 60;
  const ticks = [];
  for (let m = range.start; m <= range.end; m += step) ticks.push(m);

  const now = minutesOfDay(new Date());
  const showNow = now >= range.start && now <= range.end;
  const shownRows = showResource ? rows.slice(0, MAX_ROWS) : rows;
  const hiddenRows = rows.length - shownRows.length;

  function open(appointment) {
    navigate(canAttend ? `/appointment/${appointment.id}` : '/schedule/appointments');
  }

  return (
    <div className={styles.timeline}>
      <div className={styles.header}>
        <span className={styles.title}>Linha do dia</span>
        <div className={styles.legend}>
          {counts.map((g) => (
            <span key={g.key} className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: g.color }} />
              <strong>{g.count}</strong> {g.label.toLowerCase()}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.chart}>
        {shownRows.map((row) => (
          <div key={row.key} className={styles.row}>
            {showResource && <span className={styles.rowLabel} title={row.label}>{row.label}</span>}
            <div className={styles.track}>
              {row.list.map((a) => {
                const from = minutesOfDay(a.date);
                const group = getStatusGroup(a.status);
                const name = GetPatientById(a.patient)?.name || 'Paciente';
                return (
                  <Tooltip
                    key={a.id}
                    arrow
                    title={`${formatTime(a.date)} · ${name} · ${STATUS_LABELS[a.status] || ''}`}
                  >
                    <button
                      type="button"
                      className={`${styles.block} ${a.status === 'doing' ? styles.blockDoing : ''}`}
                      style={{
                        left: `${toPercent(from)}%`,
                        width: `${(getDurationMinutes(a.duration) / span) * 100}%`,
                        background: group.color,
                      }}
                      onClick={() => open(a)}
                      aria-label={`${formatTime(a.date)} ${name}`}
                    />
                  </Tooltip>
                );
              })}
              {showNow && <span className={styles.now} style={{ left: `${toPercent(now)}%` }} aria-hidden="true" />}
            </div>
          </div>
        ))}

        <div className={styles.row}>
          {showResource && <span className={styles.rowLabel} />}
          <div className={styles.axis}>
            {ticks.map((m) => (
              <span key={m} className={styles.tick} style={{ left: `${toPercent(m)}%` }}>
                {String(Math.floor(m / 60)).padStart(2, '0')}h
              </span>
            ))}
          </div>
        </div>
      </div>

      {hiddenRows > 0 && (
        <button type="button" className={styles.more} onClick={() => navigate('/schedule/appointments')}>
          +{hiddenRows} {hiddenRows === 1 ? 'agenda' : 'agendas'}
        </button>
      )}
    </div>
  );
}

export default DayTimeline;
